import {ReactElement, useEffect} from "react";
import {Controller} from "react-hook-form";
import styled from "styled-components";

import {InputLabel} from "@/components/Formulaire/Input/Input";
import {LoadingOrError} from "@/components/LoadingOrError/LoadingOrError";
import {AppContext} from "@/context/AppContext";
import {HookFormContext, HookFormContextType} from "@/context/HookFormContext";
import {
  UserParamContext,
  UserParamContextType,
} from "@/context/UserParamContext";
import {breakpoints, quicksandSemiBold} from "@/GlobalStyle";
import {useGetContext} from "@/hooks/useGetContext/useGetContext";
import {useLoadingState} from "@/hooks/useLoadingState/useLoadingState";
import {AppContext as AppContextType} from "@/ts/appContext";

import {BlocContent} from "../../BlocContent/BlocContent";
import SelectCustom from "../../Formulaire/SelectCustom/SelectCustom";

const ModuleSelectCampagne = styled.form`
  margin: 10px 0;
  width: 100%;
  display: flex;
  justify-content: space-around;
  align-items: center;
  row-gap: 1.25rem;
  column-gap: 1.75rem;
  flex-wrap: wrap;
  & #select-contremarque,
  #select-campagne {
    flex: 1;
    max-width: 300px;
    min-width: 200px;
    flex-direction: row;
    flex-wrap: wrap;
  }
`;
const SelectContainer = styled.div`
  flex: 1;
  max-height: 80px;
  justify-content: center;
  align-items: center;
  display: flex;
  min-width: fit-content;
`;

const CheckboxContainer = styled.div`
  flex: 1;

  flex-direction: column;
  gap: 10px;
  justify-content: center;
  align-items: center;
  display: flex;
  min-width: fit-content;
  @media (max-width: ${breakpoints.sm}) {
    min-width: 100%;
  }
`;

const InputContent = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
  align-self: center;
  margin: 0 auto;
  padding: 0 10px;
  @media (max-width: ${breakpoints.sm}) {
    flex-direction: column;
    gap: 10px;
  }
`;

const LabelContrat = styled.p<{$isDisabled?: boolean}>`
  color: ${({theme}) => theme.colors.darkPrimary};
  ${quicksandSemiBold}
  white-space: nowrap;
  ${({$isDisabled, theme}) =>
    $isDisabled &&
    `color: ${theme.colors.shadow};
    user-select: none;
    cursor: not-allowed !important;
  `}
`;
type StatutCampagne = "EnCours" | "Clôture" | "AVenir" | "Tous";

export function ModuleSelectCampagneContremarque({
  moduleSelectNeedContremarque = false,
  lockCampagne = false,
  lockContremarque = false,
}: {
  moduleSelectNeedContremarque: boolean;
  lockCampagne: boolean;
  lockContremarque: boolean;
}): ReactElement {
  const {
    currentCampagne,
    currentContremarque,
    handleSetCurrentContremarque,
    handleSetCurrentCampagne,
    allCampagnes,
    fetchAllCampagnes,
    allCampagnesInAllStatus,
    handleFilterCampagnesByStatus,
    allContremarques,
    fetchAllContremarques,
  } = useGetContext<UserParamContextType>(UserParamContext, "UserParamContext");
  const {control, watch, setValue, getValues, register} =
    useGetContext<HookFormContextType>(HookFormContext, "HookFormContext");
  const {userType} = useGetContext<AppContextType>(AppContext, "AppContext");
  const loadingStateContremarque = useLoadingState();
  const loadingStateCampagne = useLoadingState();
  // permet de savoir si on a besoin de charger les contremarques
  const needContremarque =
    userType === "Adherent" || moduleSelectNeedContremarque;

  const suiviStatusContrat = watch("status_contrat");
  useEffect(() => {
    if (allContremarques === undefined && needContremarque) {
      loadingStateContremarque.executeWithLoading(async (): Promise<void> => {
        await fetchAllContremarques();
      });
    }
    setValue(
      "status_contrat",
      localStorage.getItem("status_contrat") || "EnCours"
    );
  }, []);

  // force la mise à jour des valeurs des selects si elles changent (arrive notament quand on charge la page et que le local storage à une valeur qui n'est pas valide)
  useEffect(() => {
    if (
      currentContremarque.value !== getValues("selected_contremarque")?.value
    ) {
      setValue("selected_contremarque", currentContremarque);
    } else if (
      currentCampagne.value !== getValues("selected_campagne")?.value
    ) {
      setValue("selected_campagne", currentCampagne);
    }
  }, [currentContremarque, currentCampagne]);

  // filtre les campagnes par statut
  useEffect(() => {
    if (suiviStatusContrat) {
      // Si on a déjà récupérer toutes les campagnes on filtre par statut sans appeler l'api
      if (allCampagnesInAllStatus) {
        handleFilterCampagnesByStatus(suiviStatusContrat as StatutCampagne);
      }
      // sinon on fait l'appel api pour les récupérer
      else {
        loadingStateCampagne.executeWithLoading(async (): Promise<void> => {
          await fetchAllCampagnes(suiviStatusContrat as StatutCampagne);
        });
      }
      localStorage.setItem("status_contrat", suiviStatusContrat);
    }
  }, [suiviStatusContrat]);

  useEffect(() => {
    // si aucune valeur n'a été charger dans le select on lui met la valeur de currentCampagne
    // currentCampagne est la valeur par défaut du context soit depuis le local storage ou la première campagne en cours
    if (
      getValues("selected_campagne")?.value === undefined &&
      currentCampagne.value !== ""
    ) {
      setValue("selected_campagne", currentCampagne);
    }
    // si aucune valeur n'a été charger dans le select on lui met la valeur de currentContremarque
    // currentContremarque est la valeur par défaut du context soit depuis le local storage ou la première contremarque de la liste
    if (
      getValues("selected_contremarque")?.value === undefined &&
      currentContremarque.value !== "" &&
      needContremarque
    ) {
      setValue("selected_contremarque", currentContremarque);
    }
  }, [currentCampagne, currentContremarque]);

  // si la liste des campagnes change on met la valeur de currentCampagne dans le select
  // ça arrive notamment quand on change status de campagne (en cours, clôturé, à venir)
  useEffect(() => {
    setValue("selected_campagne", currentCampagne);
  }, [allCampagnes]);

  return (
    <BlocContent>
      <ModuleSelectCampagne>
        <CheckboxContainer>
          <LabelContrat $isDisabled={lockCampagne}>
            Voir les campagnes :
          </LabelContrat>
          <InputContent>
            {userType === "Collaborateur" ? (
              <InputLabel
                type="radio"
                label="A venir"
                isInColumn={false}
                reverseOrder={true}
                value={"AVenir"}
                disabled={lockCampagne}
                {...register("status_contrat")}
              />
            ) : null}
            <InputLabel
              type="radio"
              label="En cours"
              isInColumn={false}
              reverseOrder={true}
              value={"EnCours"}
              disabled={lockCampagne}
              {...register("status_contrat")}
            />
            <InputLabel
              type="radio"
              label="Clôturées"
              isInColumn={false}
              reverseOrder={true}
              value={"Clôture"}
              disabled={lockCampagne}
              {...register("status_contrat")}
            />
          </InputContent>
        </CheckboxContainer>
        <SelectContainer>
          <LoadingOrError
            loadingState={loadingStateCampagne}
            buttonToRefresh={true}
            onRetry={async (): Promise<void> => {
              await loadingStateCampagne.executeWithLoading(
                async () =>
                  await fetchAllCampagnes(suiviStatusContrat as StatutCampagne)
              );
            }}
            data={allCampagnes}>
            <Controller
              name="selected_campagne"
              control={control}
              render={({field}) => (
                <SelectCustom
                  id="select-campagne"
                  label={`Campagne ${allCampagnes?.length ? "(" + allCampagnes?.length + ")" : ""}`}
                  disabled={
                    !allCampagnes || allCampagnes.length <= 0 || lockCampagne
                  }
                  title={
                    !allCampagnes || allCampagnes.length <= 0
                      ? "Aucune campagne disponible pour le moment"
                      : ""
                  }
                  handleSelect={(value, libelle) => {
                    const newCampagne = {
                      libelle: libelle,
                      value: value,
                    };

                    // Déclenche le changement du champ via field.onChange
                    field.onChange(newCampagne);
                    handleSetCurrentCampagne({libelle, value});
                  }}
                  options={allCampagnes ? allCampagnes : []}
                  watch={watch}
                  {...field}
                />
              )}
            />
          </LoadingOrError>
        </SelectContainer>
        {userType === "Adherent" || moduleSelectNeedContremarque ? (
          <SelectContainer>
            <LoadingOrError
              loadingState={loadingStateContremarque}
              buttonToRefresh={true}
              onRetry={async (): Promise<void> => {
                await loadingStateContremarque.executeWithLoading(
                  fetchAllContremarques
                );
              }}
              data={allContremarques}>
              <Controller
                name="selected_contremarque"
                control={control}
                render={({field}) => (
                  <SelectCustom
                    id="select-contremarque"
                    label={`Contremarque ${allContremarques?.length ? "(" + allContremarques?.length + ")" : ""}`}
                    disabled={
                      !allContremarques ||
                      allContremarques.length <= 0 ||
                      lockContremarque
                    }
                    title={
                      !allContremarques || allContremarques.length <= 0
                        ? "Aucune contremarque disponible pour le moment"
                        : ""
                    }
                    handleSelect={(value, libelle) => {
                      const newContremarque = {
                        libelle: libelle,
                        value: value,
                      };

                      // Déclenche le changement du champ via field.onChange
                      field.onChange(newContremarque);
                      handleSetCurrentContremarque({libelle, value});
                    }}
                    options={allContremarques ? allContremarques : []}
                    watch={watch}
                    {...field}
                  />
                )}
              />
            </LoadingOrError>
          </SelectContainer>
        ) : null}
      </ModuleSelectCampagne>
    </BlocContent>
  );
}
